import type { Preferences, PreferencesChannel, PreferencesRoom } from "sdk";
import { logger } from "@/utils/logger";
import { BaseService } from "../core/Service";

const log = logger.for("api/preferences");

export const DEFAULT_PREFERENCES: Preferences = {
	frontend: {
		desktop_notifs: true,
		push_notifs: true,
		tts_notifs: false,
		message_style: "cozy",
	},
	notifs: {
		messages: "Nothing",
		threads: "Nothing",
		reactions: "Dms",
		tts: "Nothing",
	},
	privacy: {
		friends: {
			pause_until: null,
			allow_everyone: true,
			allow_mutual_friend: true,
			allow_mutual_room: true,
		},
		dms: true,
		rpc: true,
		exif: true,
	},
};

// TODO: store preferences in db instead of localstorage
// NOTE: when this is implemented, make sure to clear preferences on log out
function _loadSavedPreferences(): Preferences | null {
	const c = localStorage.getItem("preferences");
	if (!c) return null;
	try {
		return JSON.parse(c);
	} catch {
		return null;
	}
}

export class PreferencesService extends BaseService<Preferences> {
	protected cacheName = "preferences";
	public _loaded = false; // TEMP: public so that store can edit it

	constructor(store: import("../core/Store").RootStore) {
		super(store);
		this.cache.set("@self", DEFAULT_PREFERENCES);
	}

	getKey(_item: Preferences): string {
		return "@self";
	}

	/** fetch preferences via http */
	async fetch(_id: string): Promise<Preferences> {
		const data = await this.retryWithBackoff<Preferences>(() =>
			this.client.http.GET("/api/v1/preferences"),
		);
		return data;
	}

	/** set preferences via http */
	async setPreferences(preferences: Preferences): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.PUT("/api/v1/preferences", { body: preferences }),
		);
	}

	/** set room notification/privacy preferences via http */
	async setRoomPreferences(
		room_id: string,
		preferences: PreferencesRoom,
	): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.PUT("/api/v1/preferences/room/{room_id}", {
				params: { path: { room_id } },
				body: preferences,
			} as any),
		);
	}

	/** set channel (thread) notification preferences via http */
	async setChannelPreferences(
		channel_id: string,
		preferences: PreferencesChannel,
	): Promise<void> {
		await this.retryWithBackoff(() =>
			this.client.http.PUT("/api/v1/preferences/channel/{channel_id}", {
				params: { path: { channel_id } },
				body: preferences,
			} as any),
		);
	}

	/** reactively read permissions */
	useRead() {
		return this.read();
	}

	read() {
		return this.cache.get("@self") ?? DEFAULT_PREFERENCES;
	}

	/** set permissions, updating localstorage and via http if necessary */
	put(prefs: Preferences) {
		localStorage.setItem("preferences", JSON.stringify(prefs));
		this.cache.set("@self", prefs);

		if (this._loaded) {
			this.setPreferences(prefs).catch((e) => {
				log.error("Failed to sync preferences to server", e);
			});
		}
	}
}
